import { useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { trackPageview } from "./utils/ga";

// MSAL imports
import { MsalProvider } from "@azure/msal-react";
import { IPublicClientApplication } from "@azure/msal-browser";
import { CustomNavigationClient } from "./utils/NavigationClient";

// App imports
import { PageLayout } from "./ui-components/PageLayout";
import { ErrorBoundary } from "./ui-components/ErrorBoundary";
import { QueryOverlay } from "./ui-components/QueryOverlay";
import { LiveKitWidget } from "./ui-components/LiveKitWidget";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes — server cache is 10 min, so 5 min client is safe
      gcTime: 10 * 60 * 1000, // Keep unused data in cache for 10 min
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});
import { Home } from "./pages/Home";
import { Profile } from "./pages/Profile";
import { Dashboard } from "./pages/Dashboard";
import { PortcoDetail } from "./pages/PortcoDetail";
import { Inventory } from "./pages/Inventory";
import { PacingHistory } from "./pages/PacingHistory";
import { SalesByItem } from "./pages/SalesByItem";
import { Admin } from "./pages/Admin";
import { EmailSchedules } from "./pages/EmailSchedules";
import { EmailHistory } from "./pages/EmailHistory";
import { EmailTemplates } from "./pages/EmailTemplates";
import { EcommerceDashboard } from "./pages/EcommerceDashboard";
import { DtcComingSoon } from "./pages/DtcComingSoon";
import { AmazonDashboard } from "./pages/AmazonDashboard";
import { AmazonComingSoon } from "./pages/AmazonComingSoon";

type AppProps = {
  pca: IPublicClientApplication | null;
};

function App({ pca }: AppProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // GA4 SPA pageview on every route change (no-op when GA is disabled)
  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // In demo mode (pca is null), render without MsalProvider
  if (!pca) {
    return (
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <PageLayout>
            <Pages />
          </PageLayout>
          <QueryOverlay />
        </ErrorBoundary>
      </QueryClientProvider>
    );
  }

  // Live mode — configure MSAL navigation and wrap in MsalProvider
  const navigationClient = new CustomNavigationClient(navigate);
  pca.setNavigationClient(navigationClient);

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <MsalProvider instance={pca}>
          <LiveKitWidget />
          <Routes>
            {/* Home/login renders full-screen — no PageLayout */}
            <Route path="/" element={<Home />} />
            {/* All other routes use PageLayout with navbar/footer */}
            <Route
              path="*"
              element={
                <PageLayout>
                  <Routes>
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route
                      path="/portco/:portcoId"
                      element={<PortcoDetail />}
                    />
                    <Route
                      path="/portco/:portcoId/inventory"
                      element={<Inventory />}
                    />
                    <Route
                      path="/portco/:portcoId/pacing"
                      element={<PacingHistory />}
                    />
                    <Route
                      path="/portco/:portcoId/sales-by-item"
                      element={<SalesByItem />}
                    />
                    <Route path="/admin" element={<Admin />} />
                    <Route
                      path="/admin/email-schedules"
                      element={<EmailSchedules />}
                    />
                    <Route
                      path="/admin/email-history"
                      element={<EmailHistory />}
                    />
                    <Route
                      path="/admin/email-templates"
                      element={<EmailTemplates />}
                    />
                    <Route
                      path="/ecommerce/:brandCode"
                      element={<EcommerceDashboard />}
                    />
                    <Route
                      path="/portco/dtc/coming-soon"
                      element={<DtcComingSoon />}
                    />
                    <Route
                      path="/portco/dtc/:brandCode"
                      element={<EcommerceDashboard />}
                    />
                    <Route
                      path="/portco/amazon/coming-soon"
                      element={<AmazonComingSoon />}
                    />
                    <Route
                      path="/portco/amazon/:brandCode"
                      element={<AmazonDashboard />}
                    />
                  </Routes>
                </PageLayout>
              }
            />
          </Routes>
        </MsalProvider>
        <QueryOverlay />
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

function Pages() {
  return (
    <Routes>
      <Route path="/profile" element={<Profile />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/portco/:portcoId" element={<PortcoDetail />} />
      <Route path="/portco/:portcoId/inventory" element={<Inventory />} />
      <Route path="/portco/:portcoId/pacing" element={<PacingHistory />} />
      <Route path="/portco/:portcoId/sales-by-item" element={<SalesByItem />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/admin/email-schedules" element={<EmailSchedules />} />
      <Route path="/admin/email-history" element={<EmailHistory />} />
      <Route path="/admin/email-templates" element={<EmailTemplates />} />
      <Route path="/ecommerce/:brandCode" element={<EcommerceDashboard />} />
      <Route path="/portco/dtc/coming-soon" element={<DtcComingSoon />} />
      <Route path="/portco/dtc/:brandCode" element={<EcommerceDashboard />} />
      <Route path="/portco/amazon/coming-soon" element={<AmazonComingSoon />} />
      <Route path="/portco/amazon/:brandCode" element={<AmazonDashboard />} />
      <Route path="/" element={<Home />} />
    </Routes>
  );
}

export default App;
